import { defineStore } from 'pinia';
import { computed, shallowRef } from 'vue';
import { Agreement, AgreementStatus, Data, agreementsService, termTypes } from '@/connect';
import { useSocketStore } from '@/store/socket-store';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import i18n from '@/localisation/i18n';
import { useCompaniesStore } from '@/store/companies-store';
import { useAuthStore } from '@/store/auth-store';

export const useAgreementsStore = defineStore('agreements', () => {
  const socketStore = useSocketStore();
  const snackbarManager = useSnackbarManager();

  // Once we migrate to Vue3 we should use shallowReactive
  const agreements = shallowRef(new Map<string, Data<Agreement>>());

  async function loadAgreements(asCompanyId?: string) {
    const result = await agreementsService.queryAgreements(
      {
        statuses: [
          AgreementStatus.AGREEMENT_STATUS_UNSPECIFIED,
          AgreementStatus.DRAFT,
          AgreementStatus.ACTIVE,
          AgreementStatus.REJECTED,
          AgreementStatus.CANCELED,
          AgreementStatus.INACTIVE,
        ],
        bookingAccounts: [],
        counterpartyBookingAccounts: [],
        counterpartyIds: [],
        tradeTypes: [],
        collateralCurrencies: [],
        deliveryTypes: [],
      },
      asCompanyId
    );

    if (!result.success) {
      snackbarManager.error(i18n.t(result.error, result.details) as string);
      return;
    }

    const agreementsNew = new Map(agreements.value);
    for (const agreement of result.data.agreements) {
      agreementsNew.set(agreement.id, agreement);
    }
    agreements.value = agreementsNew;
  }

  async function refreshAgreements() {
    await loadAgreements();
  }

  socketStore.useEventHandler('refreshAgreements', refreshAgreements);

  function getAgreement(agreementId: string | undefined) {
    return agreementId === undefined ? undefined : agreements.value.get(agreementId);
  }

  function getAgreementDetails(agreementId: string) {
    return getAgreementDetailsFromAgreement(getAgreement(agreementId));
  }

  return {
    getAgreement,
    getAgreementDetails,
    loadAgreements,
    refreshAgreements,
    agreements: computed(() => [...agreements.value.values()]),
  };
});

export function getAgreementDetailsFromAgreement(agreement: Data<Agreement> | undefined): string {
  const authStore = useAuthStore();
  const companiesStore = useCompaniesStore();

  if (agreement === undefined) return 'Unknown agreement';
  let result = agreement.id;
  if (agreement.shortName) {
    result = `${agreement.shortName} | ${result}`;
  }

  if (authStore.user) {
    const counterparty = companiesStore.getCompany(agreement.counterpartyId);
    if (counterparty !== undefined) {
      result += ` | ${counterparty.companyName}`;
    }
  }
  result += ` | Term Type: ${termTypes.get(agreement.termType)}`;
  return result;
}

export type AgreementsStore = ReturnType<typeof useAgreementsStore>;
